import React, { useState, useMemo } from 'react';
import { UserProfile } from '../types';
import { AppSettings } from '../lib/storage';
import { 
  User, Shield, MessageSquare, Bell, Database, Globe, Users, Sparkles, 
  Search, ChevronRight, X, LogOut, Crown
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

import { AccountSection } from './settings/AccountSection';
import { PrivacySection } from './settings/PrivacySection';
import { ChatSettingsSection } from './settings/ChatSettingsSection';
import { NotificationsSection } from './settings/NotificationsSection';
import { DataStorageSection } from './settings/DataStorageSection';
import { LanguageAppearanceSection } from './settings/LanguageAppearanceSection';
import { ContactsSettingsSection } from './settings/ContactsSettingsSection';
import { AISettingsSection } from './settings/AISettingsSection';

interface SettingsViewProps {
  currentUser: UserProfile;
  appSettings: AppSettings;
  onUpdateSettings: (settings: Partial<AppSettings>) => void;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
  onLogout: () => void;
  onTerminateSession: (deviceId: string) => void;
  onLogoutAllDevices: () => void;
  onStartDirectChat?: (username: string) => void;
  onOpenPremium?: () => void;
}

type SettingTabId = 'account' | 'privacy' | 'chats' | 'notifications' | 'data' | 'appearance' | 'contacts' | 'ai';

export const SettingsView: React.FC<SettingsViewProps> = ({
  currentUser, appSettings, onUpdateSettings, onUpdateProfile, onLogout, onTerminateSession, onLogoutAllDevices
}) => {
  const [activeTab, setActiveTab] = useState<SettingTabId>('account');
  const [searchQuery, setSearchQuery] = useState('');

  const tabs: { id: SettingTabId; label: string; icon: React.ElementType; color: string; keywords: string[] }[] = [
    { id: 'account', label: 'الحساب', icon: User, color: 'text-emerald-400', keywords: ['حساب', 'صورة', 'اسم', 'username', 'جلسات', 'أجهزة', 'تسجيل خروج'] },
    { id: 'privacy', label: 'الخصوصية والأمان', icon: Shield, color: 'text-rose-400', keywords: ['خصوصية', 'أمان', 'حظر', 'ظهور', 'متصل', 'رقم', '2fa', 'security', 'privacy'] },
    { id: 'chats', label: 'المحادثات', icon: MessageSquare, color: 'text-indigo-400', keywords: ['محادثات', 'خط', 'خلفية', 'ثيم', 'رسائل', 'حذف', 'وسائط', 'chat'] },
    { id: 'notifications', label: 'الإشعارات', icon: Bell, color: 'text-amber-400', keywords: ['إشعارات', 'صوت', 'اهتزاز', 'تنبيه', 'كتم', 'مكالمات', 'notification'] },
    { id: 'data', label: 'البيانات والتخزين', icon: Database, color: 'text-blue-400', keywords: ['بيانات', 'تخزين', 'تنزيل', 'واي فاي', 'wifi', 'مؤقتة', 'cache', 'data', 'storage'] },
    { id: 'appearance', label: 'اللغة والمظهر', icon: Globe, color: 'text-teal-400', keywords: ['لغة', 'مظهر', 'داكن', 'فاتح', 'ثيم', 'عربية', 'english', 'language', 'theme'] },
    { id: 'contacts', label: 'جهات الاتصال', icon: Users, color: 'text-emerald-400', keywords: ['جهات', 'اتصال', 'أصدقاء', 'دعوة', 'مزامنة', 'contacts'] },
    { id: 'ai', label: 'وتين الذكي', icon: Sparkles, color: 'text-fuchsia-400', keywords: ['وتين', 'ذكاء', 'اصطناعي', 'ai', 'مساعد', 'ترجمة', 'ردود', 'bot'] },
  ];

  const filteredTabs = useMemo(() => {
    if (!searchQuery.trim()) return tabs;
    const lowerQ = searchQuery.toLowerCase();
    return tabs.filter(t => 
      t.label.toLowerCase().includes(lowerQ) || 
      t.keywords.some(k => k.toLowerCase().includes(lowerQ))
    );
  }, [searchQuery]);

  const renderActiveSection = () => {
    switch (activeTab) {
      case 'account': return <AccountSection currentUser={currentUser} onUpdateProfile={onUpdateProfile} onLogout={onLogout} onTerminateSession={onTerminateSession} onLogoutAllDevices={onLogoutAllDevices} />;
      case 'privacy': return <PrivacySection currentUser={currentUser} onUpdateProfile={onUpdateProfile} />;
      case 'chats': return <ChatSettingsSection currentUser={currentUser} onUpdateProfile={onUpdateProfile} />;
      case 'notifications': return <NotificationsSection currentUser={currentUser} onUpdateProfile={onUpdateProfile} />;
      case 'data': return <DataStorageSection currentUser={currentUser} onUpdateProfile={onUpdateProfile} />;
      case 'appearance': return <LanguageAppearanceSection currentUser={currentUser} appSettings={appSettings} onUpdateSettings={onUpdateSettings} />;
      case 'contacts': return <ContactsSettingsSection currentUser={currentUser} onUpdateProfile={onUpdateProfile} />;
      case 'ai': return <AISettingsSection currentUser={currentUser} onUpdateProfile={onUpdateProfile} />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-full bg-[#060911] font-['Cairo'] relative select-none">
      
      {/* SIDEBAR (Desktop) / TOP NAV (Mobile) */}
      <div className="md:w-80 flex-shrink-0 bg-[#0a0f1b]/95 backdrop-blur-xl border-b md:border-b-0 md:border-l border-slate-800/80 flex flex-col h-auto md:h-full z-20 transition-all">
        {/* Header & Search */}
        <div className="p-4 md:p-6 pb-2 md:pb-4 border-b border-slate-800/50">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4">الإعدادات</h2>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث في الإعدادات..."
              className="w-full bg-[#121927] border border-slate-700/80 rounded-xl pr-10 pl-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs List */}
        <div className="flex-1 overflow-y-auto no-scrollbar p-2 md:p-3 space-y-1 hidden md:block">
          {filteredTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { soundEffects.playTap(); setActiveTab(tab.id); }}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                  isActive 
                    ? 'bg-[#141c2a] border border-slate-700/50 shadow-md' 
                    : 'hover:bg-[#141c2a]/50 text-slate-400 border border-transparent'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isActive ? 'bg-[#0e1422]' : 'bg-transparent'}`}>
                    <Icon className={`w-5 h-5 ${isActive ? tab.color : 'text-slate-400'}`} />
                  </div>
                  <span className={`font-semibold ${isActive ? 'text-white' : 'text-slate-300'}`}>{tab.label}</span>
                </div>
                {isActive && <ChevronRight className="w-4 h-4 text-emerald-500" />}
              </button>
            )
          })}
          
          <div className="h-px bg-slate-800 my-4 mx-2"></div>
          
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 p-3 rounded-xl text-rose-400 hover:bg-rose-500/10 transition border border-transparent"
          >
            <div className="p-2 rounded-lg">
              <LogOut className="w-5 h-5" />
            </div>
            <span className="font-bold">تسجيل الخروج</span>
          </button>
        </div>

        {/* Mobile Horizontal Tabs */}
        <div className="flex md:hidden overflow-x-auto no-scrollbar p-2 gap-2 touch-pan-x border-b border-slate-800/50">
          {filteredTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { soundEffects.playTap(); setActiveTab(tab.id); }}
                className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl transition-all border ${
                  isActive 
                    ? 'bg-[#141c2a] border-slate-700/50 text-white shadow-md' 
                    : 'bg-transparent border-transparent text-slate-400'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? tab.color : 'text-slate-400'}`} />
                <span className="text-xs font-semibold whitespace-nowrap">{tab.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 overflow-y-auto no-scrollbar p-4 md:p-8 md:pl-20 relative">
        <div className="max-w-3xl mx-auto pb-20 md:pb-8">
          {filteredTabs.length === 0 ? (
            <div className="text-center text-slate-500 mt-20">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>لم يتم العثور على أي إعداد يطابق بحثك.</p>
            </div>
          ) : (
            renderActiveSection()
          )}
        </div>
      </div>

    </div>
  );
};
